import { match, matchPath } from 'react-router'
import { createSelector } from 'reselect'
import {
  ApprovalRuleResponse,
  ApprovalRuleType,
  OrganizationInviteModel,
  OrganizationInviteResponse,
  OrganizationsListModel,
  OrganizationsListResponse,
} from 'src/models/organizations.models'
import {
  DepartmentDetailsMatchParams,
  departmentDetailsRouteProps,
  ManageOrgInviteMatchParams,
  manageOrgInviteRouteProps,
  organizationSettingsRouteProps,
  OrganizationsMatchParams,
} from 'src/navigation/routeProps'
import { RootState } from 'src/redux/createRootReducer'
import { selectUserListModelFromState } from 'src/redux/selectors/auth.selectors'
import { selectMostRecentlyCreatedCreditCardListModelFromState } from 'src/redux/selectors/data/creditCards.selectors'
import { selectFeatureFlaggerFromState } from 'src/redux/selectors/data/featureFlagger.selectors'
import { selectOrgIntegrationBetaOrgsFromRemoteConfigState } from 'src/redux/selectors/data/remoteConfig.selectors'
import { selectLocationFromState } from 'src/redux/selectors/router.selectors'
import { isAIInsightsEnabled, isExtendAIEnabled } from 'src/utils/featureFlags'

export const selectOrganizationsListResponsesFromState = (
  state: RootState,
): Record<string, OrganizationsListResponse> =>
  state.data.organizations.listResponses

export const selectNumberOfOrganizationsFromState = (
  state: RootState,
): number => state.data.organizations.totalItems || 0

export const selectOrganizationInvitesListResponsesFromState = (
  state: RootState,
): Record<string, OrganizationInviteResponse> =>
  state.data.organizations.organizationInvites.listResponses

export const selectOrganizationsListModelsFromState = createSelector(
  [selectOrganizationsListResponsesFromState],
  (organizationsListResponses) =>
    Object.values(organizationsListResponses).reduce(
      (acc, curr) => ({
        ...acc,
        [curr.id]: new OrganizationsListModel(curr),
      }),
      {} as Record<string, OrganizationsListModel>,
    ),
)

export const selectOrganizationInvitesModelsFromState = createSelector(
  [selectOrganizationInvitesListResponsesFromState],
  (OrganizationInviteResponses) =>
    Object.values(OrganizationInviteResponses).reduce(
      (acc, curr) => ({
        ...acc,
        [curr.id]: new OrganizationInviteModel(curr),
      }),
      {} as Record<string, OrganizationInviteModel>,
    ),
)

export const selectExplicitOrganizationsListModelsFromState = createSelector(
  [selectOrganizationsListResponsesFromState],
  (organizationsListResponses) =>
    Object.values(organizationsListResponses).reduce(
      (acc, curr) =>
        curr.explicit
          ? {
              ...acc,
              [curr.id]: new OrganizationsListModel(curr),
            }
          : acc,
      {} as Record<string, OrganizationsListModel>,
    ),
)

export const selectUserExplicitOrganizationListModelFromState = createSelector(
  [
    selectExplicitOrganizationsListModelsFromState,
    selectUserListModelFromState,
  ],
  (organizations, user) =>
    organizations !== undefined &&
    user?.organizationId !== undefined &&
    organizations[user.organizationId] !== undefined
      ? new OrganizationsListModel(organizations[user.organizationId])
      : undefined,
)

export const selectMostRecentlyCreatedCreditCardOrganizationListModelFromState =
  createSelector(
    [
      selectOrganizationsListResponsesFromState,
      selectMostRecentlyCreatedCreditCardListModelFromState,
    ],
    (organizations, creditCard) =>
      organizations !== undefined &&
      creditCard !== undefined &&
      creditCard.user?.organizationId !== undefined
        ? organizations[creditCard.user.organizationId]
        : undefined,
  )

export const selectOrganizationIdFromRoute = createSelector(
  [selectLocationFromState],
  (location) => {
    const mtch = matchPath<OrganizationsMatchParams>(
      location.pathname,
      organizationSettingsRouteProps,
    ) as match<OrganizationsMatchParams>
    return mtch?.params.id
  },
)

export const selectDepartmentIdFromRoute = createSelector(
  [selectLocationFromState],
  (location) => {
    const mtch = matchPath<DepartmentDetailsMatchParams>(
      location.pathname,
      departmentDetailsRouteProps,
    ) as match<DepartmentDetailsMatchParams>
    return mtch?.params.departmentId
  },
)

export const selectOrganizationInviteIdFromRoute = createSelector(
  [selectLocationFromState],
  (location) => {
    const mtch = matchPath<ManageOrgInviteMatchParams>(
      location.pathname,
      manageOrgInviteRouteProps,
    ) as match<ManageOrgInviteMatchParams>
    return mtch?.params.id
  },
)

export const selectOrganizationInviteFromState = createSelector(
  [
    selectOrganizationInvitesListResponsesFromState,
    selectOrganizationInviteIdFromRoute,
  ],
  (organizationInvites, inviteId) =>
    Object.values(organizationInvites).find(
      (organizationInvite) => organizationInvite.id === inviteId,
    ),
)

export const selectOrganizationReceiptMgmtEnabledFromState = (
  state: RootState,
): boolean | undefined =>
  state.data.organizations.organizationReceiptRules.enabled

export const selectOrganizationReceiptMgmtIsLoadedFromState = (
  state: RootState,
): boolean => state.data.organizations.organizationReceiptRules.isLoaded

// TODO: investigate whether we can refactor this or not
export const selectOrganizationTopLevelErrorFromState = (
  state: RootState,
): string | undefined => state.data.organizations.topLevelError

export const selectIsOrganizationBeingEditedFromState = (
  state: RootState,
): boolean | undefined => state.data.organizations.isOrganizationBeingEdited

export const selectOutOfPocketExpensesEnabledFromState = (
  state: RootState,
): boolean | undefined => state.data.organizations.outOfPocketExpensesEnabled

export const selectCardApprovalsEnabled = (state: RootState): boolean => {
  if (state.data?.organizations?.cardApprovalRules) {
    return (
      state.data?.organizations?.cardApprovalRules?.type ===
      ApprovalRuleType.Approver
    )
  } else {
    return false
  }
}

export const selectCardApprovalsIsFetched = (state: RootState): boolean => {
  return state.data?.organizations?.cardApprovalsIsFetched
}

export const selectCardApprovalsFromState = (
  state: RootState,
): ApprovalRuleResponse | undefined => {
  if (state.data?.organizations?.cardApprovalRules)
    return state.data.organizations.cardApprovalRules
}

export const selectOutOfPocketExpensesVisible = createSelector(
  [
    selectUserExplicitOrganizationListModelFromState,
    selectOutOfPocketExpensesEnabledFromState,
  ],
  (organization, enabled) =>
    organization !== undefined
      ? enabled && organization.isOutOfPocketExpensesVisible
      : false,
)

export const selectIsBetaOrgIntegrationOrg = createSelector(
  [
    selectUserExplicitOrganizationListModelFromState,
    selectOrgIntegrationBetaOrgsFromRemoteConfigState,
  ],
  (organization, betaOrgIds) => {
    return (
      organization?.id !== undefined && betaOrgIds?.includes(organization?.id)
    )
  },
)

export const selectIsBetaOrgAgent = createSelector(
  [
    selectFeatureFlaggerFromState,
    selectUserExplicitOrganizationListModelFromState,
  ],
  (remoteConfig, organization) => {
    return isExtendAIEnabled(remoteConfig, organization?.id)
  },
)

export const selectIsAIInsightsEnabled = createSelector(
  [
    selectFeatureFlaggerFromState,
    selectUserExplicitOrganizationListModelFromState,
  ],
  (remoteConfig, organization) => {
    return isAIInsightsEnabled(remoteConfig, organization?.id)
  },
)

export const selectAllOrgIntegrations = createSelector(
  [selectUserExplicitOrganizationListModelFromState],
  (organization) => organization?.integrations,
)
